import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '../../services/api';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// --- Types ---

interface Property {
  id: number;
  title: string;
}

interface UserOption {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}

interface UsersSearchResponse {
  data: UserOption[];
}

interface PropertiesResponse {
  data: Property[];
}

interface ContractDetail {
  id: number;
  property_id: number;
  tenant_user_id: number;
  contract_type: string;
  start_date: string;
  end_date: string;
  monthly_amount: number;
  currency: string;
  deposit_amount: number;
  terms: string;
  status: string;
}

interface ContractResponse {
  data: ContractDetail;
}

// --- Constants ---

const CONTRACT_TYPES = [
  { value: 'rent', label: 'Alquiler' },
  { value: 'sale', label: 'Venta' },
  { value: 'reservation', label: 'Reserva' },
] as const;

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'link'];

// --- Schema ---

const contractSchema = z.object({
  property_id: z.coerce.number().min(1, 'Selecciona una propiedad'),
  tenant_user_id: z.string().min(1, 'Ingresa el ID del inquilino'),
  contract_type: z.enum(['rent', 'sale', 'reservation'], {
    message: 'Selecciona el tipo de contrato',
  }),
  start_date: z.string().min(1, 'Ingresa la fecha de inicio'),
  end_date: z.string().min(1, 'Ingresa la fecha de fin'),
  monthly_amount: z.coerce.number().min(0, 'El monto debe ser mayor o igual a 0'),
  currency: z.enum(['ARS', 'USD']).default('ARS'),
  deposit_amount: z.coerce.number().min(0).default(0),
  terms: z.string().optional().or(z.literal('')),
});

type ContractFormData = z.infer<typeof contractSchema>;
type ContractFormInput = z.input<typeof contractSchema>;

// --- Component ---

export default function ContractFormPage(): React.ReactElement {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const isEdit = !!id;
  const preselectedPropertyId = searchParams.get('property_id');

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Auto-dismiss snackbar
  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => setSnackbar((s) => ({ ...s, open: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [snackbar.open]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<ContractFormInput, unknown, ContractFormData>({
    resolver: zodResolver(contractSchema),
    defaultValues: {
      property_id: preselectedPropertyId ? Number(preselectedPropertyId) : 0,
      tenant_user_id: '',
      contract_type: undefined,
      start_date: '',
      end_date: '',
      monthly_amount: 0,
      currency: 'ARS',
      deposit_amount: 0,
      terms: '',
    },
  });

  // Tenant search state
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantDropdownOpen, setTenantDropdownOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<UserOption | null>(null);
  const tenantRef = useRef<HTMLDivElement>(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent): void => {
      if (tenantRef.current && !tenantRef.current.contains(e.target as Node)) {
        setTenantDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch users for tenant search
  const { data: usersData } = useQuery({
    queryKey: ['users-search', tenantSearch],
    queryFn: () => api.get<UsersSearchResponse>('/v1/users/search', { q: tenantSearch, limit: 15 }),
    enabled: tenantSearch.length >= 1 || tenantDropdownOpen,
  });

  const userOptions = usersData?.data ?? [];

  // Fetch properties for select
  const { data: propertiesData } = useQuery({
    queryKey: ['properties-list'],
    queryFn: () => api.get<PropertiesResponse>('/v1/properties', { page_size: 200 }),
  });

  const properties = propertiesData?.data ?? [];

  // Fetch existing contract for edit
  const { data: existingContract, isLoading: isLoadingContract } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => api.get<ContractResponse>(`/v1/contracts/${id}`),
    enabled: isEdit,
  });

  // Populate form with existing data
  useEffect(() => {
    if (existingContract?.data) {
      const c = existingContract.data;
      reset({
        property_id: c.property_id,
        tenant_user_id: String(c.tenant_user_id),
        contract_type: c.contract_type as 'rent' | 'sale' | 'reservation',
        start_date: c.start_date ? c.start_date.slice(0, 10) : '',
        end_date: c.end_date ? c.end_date.slice(0, 10) : '',
        monthly_amount: c.monthly_amount || 0,
        currency: (c.currency as 'ARS' | 'USD') || 'ARS',
        deposit_amount: c.deposit_amount || 0,
        terms: c.terms || '',
      });
      // Load tenant info for display
      if (c.tenant_user_id && !selectedTenant) {
        api.get<UsersSearchResponse>('/v1/users/search', { q: '', limit: 100 }).then((res) => {
          const found = res?.data?.find((u: UserOption) => u.id === c.tenant_user_id);
          if (found) setSelectedTenant(found);
        }).catch(() => {});
      }
    }
  }, [existingContract, reset]); // eslint-disable-line react-hooks/exhaustive-deps

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: ContractFormData) =>
      api.post<ContractResponse>('/v1/contracts', {
        ...data,
        tenant_user_id: Number(data.tenant_user_id),
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      setSnackbar({ open: true, message: 'Borrador guardado correctamente', severity: 'success' });
      navigate(`/app/contracts/${response.data.id}/edit`, { replace: true });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Error al guardar el contrato', severity: 'error' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: ContractFormData) =>
      api.patch<ContractResponse>(`/v1/contracts/${id}`, {
        ...data,
        tenant_user_id: Number(data.tenant_user_id),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contract', id] });
      setSnackbar({ open: true, message: 'Borrador actualizado correctamente', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Error al actualizar el contrato', severity: 'error' });
    },
  });

  // Send for signature mutation
  const sendForSignatureMutation = useMutation({
    mutationFn: () => api.patch(`/v1/contracts/${id}/send-for-signature`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      queryClient.invalidateQueries({ queryKey: ['contract', id] });
      setSnackbar({ open: true, message: 'Contrato enviado a firma correctamente', severity: 'success' });
      setTimeout(() => navigate('/app/contracts'), 1500);
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Error al enviar a firma', severity: 'error' });
    },
  });

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const saveDraft = (data: ContractFormData): void => {
    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const handleSendForSignature = (): void => {
    if (!id) return;
    // Save first, then send for signature
    const data = getValues() as unknown as ContractFormData;
    if (isEdit) {
      updateMutation.mutate(data, {
        onSuccess: () => {
          sendForSignatureMutation.mutate();
        },
      });
    }
  };

  const canSendForSignature = isEdit && existingContract?.data?.status === 'draft';

  if (isEdit && isLoadingContract) {
    return (
      <div className="flex justify-center p-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button className="btn btn-ghost" onClick={() => navigate('/app/contracts')}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Volver
        </button>
        <h1 className="text-3xl font-bold">
          {isEdit ? 'Editar contrato' : 'Nuevo contrato'}
        </h1>
      </div>

      {/* Form Card */}
      <div className="card bg-base-100 shadow-sm max-w-3xl mx-auto">
        <div className="card-body">
          <form onSubmit={handleSubmit(saveDraft)} noValidate>
            <div className="flex flex-col gap-4">
              {/* Property select */}
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">Propiedad *</span>
                </label>
                <select
                  className={`select select-bordered w-full ${errors.property_id ? 'select-error' : ''}`}
                  {...register('property_id')}
                >
                  <option value={0} disabled>Seleccionar propiedad</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </select>
                {errors.property_id && (
                  <label className="label">
                    <span className="label-text-alt text-error">{errors.property_id.message}</span>
                  </label>
                )}
              </div>

              {/* Tenant user - searchable dropdown */}
              <div className="form-control w-full" ref={tenantRef}>
                <label className="label">
                  <span className="label-text">Inquilino / Cliente *</span>
                </label>
                {selectedTenant ? (
                  <div className="flex items-center gap-2">
                    <div className="input input-bordered w-full flex items-center justify-between">
                      <span>
                        {selectedTenant.first_name} {selectedTenant.last_name}
                        <span className="text-base-content/50 ml-2 text-sm">({selectedTenant.email})</span>
                      </span>
                    </div>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        setSelectedTenant(null);
                        setTenantSearch('');
                        setValue('tenant_user_id', '', { shouldValidate: true });
                      }}
                    >
                      Cambiar
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      className={`input input-bordered w-full ${errors.tenant_user_id ? 'input-error' : ''}`}
                      placeholder="Buscar por nombre o email..."
                      value={tenantSearch}
                      onChange={(e) => {
                        setTenantSearch(e.target.value);
                        setTenantDropdownOpen(true);
                      }}
                      onFocus={() => setTenantDropdownOpen(true)}
                    />
                    {tenantDropdownOpen && userOptions.length > 0 && (
                      <ul className="absolute z-20 top-full left-0 right-0 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {userOptions.map((u) => (
                          <li key={u.id}>
                            <button
                              type="button"
                              className="w-full text-left px-4 py-3 hover:bg-base-200 transition-colors flex items-center justify-between"
                              onClick={() => {
                                setSelectedTenant(u);
                                setTenantDropdownOpen(false);
                                setTenantSearch('');
                                setValue('tenant_user_id', String(u.id), { shouldValidate: true });
                              }}
                            >
                              <div>
                                <span className="font-medium">{u.first_name} {u.last_name}</span>
                                <span className="text-base-content/50 text-sm ml-2">{u.email}</span>
                              </div>
                              <span className="badge badge-outline badge-sm">
                                {u.role === 'end_user' ? 'Cliente' : u.role === 'agent' ? 'Agente' : u.role === 'company_admin' ? 'Admin' : u.role}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {tenantDropdownOpen && tenantSearch.length >= 1 && userOptions.length === 0 && (
                      <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-base-100 border border-base-300 rounded-lg shadow-lg p-4 text-center text-base-content/50 text-sm">
                        No se encontraron usuarios
                      </div>
                    )}
                  </div>
                )}
                {/* Hidden input for react-hook-form */}
                <input type="hidden" {...register('tenant_user_id')} />
                {errors.tenant_user_id && (
                  <label className="label">
                    <span className="label-text-alt text-error">{errors.tenant_user_id.message}</span>
                  </label>
                )}
              </div>

              {/* Contract type */}
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">Tipo de contrato *</span>
                </label>
                <select
                  className={`select select-bordered w-full ${errors.contract_type ? 'select-error' : ''}`}
                  {...register('contract_type')}
                >
                  <option value="" disabled>Seleccionar tipo</option>
                  {CONTRACT_TYPES.map((ct) => (
                    <option key={ct.value} value={ct.value}>
                      {ct.label}
                    </option>
                  ))}
                </select>
                {errors.contract_type && (
                  <label className="label">
                    <span className="label-text-alt text-error">{errors.contract_type.message}</span>
                  </label>
                )}
              </div>

              {/* Date range */}
              <div className="flex gap-4">
                <div className="form-control flex-1">
                  <label className="label">
                    <span className="label-text">Fecha de inicio *</span>
                  </label>
                  <input
                    type="date"
                    className={`input input-bordered w-full ${errors.start_date ? 'input-error' : ''}`}
                    {...register('start_date')}
                  />
                  {errors.start_date && (
                    <label className="label">
                      <span className="label-text-alt text-error">{errors.start_date.message}</span>
                    </label>
                  )}
                </div>
                <div className="form-control flex-1">
                  <label className="label">
                    <span className="label-text">Fecha de fin *</span>
                  </label>
                  <input
                    type="date"
                    className={`input input-bordered w-full ${errors.end_date ? 'input-error' : ''}`}
                    {...register('end_date')}
                  />
                  {errors.end_date && (
                    <label className="label">
                      <span className="label-text-alt text-error">{errors.end_date.message}</span>
                    </label>
                  )}
                </div>
              </div>

              {/* Amount + currency */}
              <div className="flex gap-4">
                <div className="form-control flex-1">
                  <label className="label">
                    <span className="label-text">Monto mensual *</span>
                  </label>
                  <label className="input input-bordered flex items-center gap-2">
                    <span className="text-base-content/50">$</span>
                    <input
                      type="number"
                      className="grow"
                      {...register('monthly_amount')}
                    />
                  </label>
                  {errors.monthly_amount && (
                    <label className="label">
                      <span className="label-text-alt text-error">{errors.monthly_amount.message}</span>
                    </label>
                  )}
                </div>
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => (
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Moneda</span>
                      </label>
                      <div className="join">
                        <button
                          type="button"
                          className={`join-item btn ${field.value === 'ARS' ? 'btn-primary' : 'btn-outline'}`}
                          onClick={() => field.onChange('ARS')}
                        >
                          ARS
                        </button>
                        <button
                          type="button"
                          className={`join-item btn ${field.value === 'USD' ? 'btn-primary' : 'btn-outline'}`}
                          onClick={() => field.onChange('USD')}
                        >
                          USD
                        </button>
                      </div>
                    </div>
                  )}
                />
              </div>

              {/* Deposit */}
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">Deposito / Garantia</span>
                </label>
                <label className="input input-bordered flex items-center gap-2">
                  <span className="text-base-content/50">$</span>
                  <input
                    type="number"
                    className="grow"
                    {...register('deposit_amount')}
                  />
                </label>
                {errors.deposit_amount && (
                  <label className="label">
                    <span className="label-text-alt text-error">{errors.deposit_amount.message}</span>
                  </label>
                )}
              </div>

              {/* Terms - Rich text */}
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text">Terminos y condiciones</span>
                </label>
                <Controller
                  name="terms"
                  control={control}
                  render={({ field }) => (
                    <ReactQuill
                      theme="snow"
                      value={field.value || ''}
                      onChange={field.onChange}
                      modules={QUILL_MODULES}
                      formats={QUILL_FORMATS}
                      placeholder="Escribe los terminos y condiciones del contrato..."
                    />
                  )}
                />
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex justify-between mt-8 pt-4 border-t border-base-200">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => navigate('/app/contracts')}
              >
                Cancelar
              </button>
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="btn btn-outline"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <span className="loading loading-spinner loading-sm" />
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z" />
                      </svg>
                      Guardar borrador
                    </>
                  )}
                </button>

                {canSendForSignature && (
                  <button
                    type="button"
                    className="btn btn-warning"
                    onClick={handleSendForSignature}
                    disabled={sendForSignatureMutation.isPending || isSaving}
                  >
                    {sendForSignatureMutation.isPending ? (
                      <span className="loading loading-spinner loading-sm" />
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                        </svg>
                        Enviar a firma
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Toast */}
      {snackbar.open && (
        <div className="toast toast-end toast-bottom z-50">
          <div className={`alert ${snackbar.severity === 'success' ? 'alert-success' : 'alert-error'}`}>
            <span>{snackbar.message}</span>
            <button className="btn btn-ghost btn-xs" onClick={() => setSnackbar((s) => ({ ...s, open: false }))}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
